import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useGoBack from "hooks/useGoBack";
import {
    getPostDetail,
    togglePostLike,
    getComments,
    createComment,
    toggleCommentLike,
    deletePost,
    deleteComment,
    updateComment,
    blockUser,
    createReport,
} from "api/community";
import { useSnackbar } from "contexts/SnackbarContext";
import useUserStore from "@store/useUserStore";
import MediaCarousel from "components/community/MediaCarousel";
import MediaViewer from "components/common/MediaViewer";
import BottomSheet from "components/common/BottomSheet";
import SubHeader from "components/common/SubHeader";

import useCommunityCategories from "hooks/useCommunityCategories";
import useRefreshCallback from "hooks/useRefreshCallback";
import { formatTime } from "utils/util";
import { getPublicUserKey, isSameUser } from "utils/userIdentity";
import { IDENTITY_STATUS, usePublicIdentityStatus } from "hooks/usePublicIdBackfill";
import { renderWithMentions } from "utils/renderWithLinks";
import MentionDropdown from "components/community/MentionDropdown";
import useMentionAutocomplete from "hooks/useMentionAutocomplete";
import { SERVER_MAX_COMMENT_CONTENT } from "utils/mentionEditor";
import "pages/community/community.scss";
import Seo from "components/common/Seo";

const CommunityDetail = () => {
    const { categoryMap } = useCommunityCategories();
    const navigate = useNavigate();
    const { postId } = useParams();
    const [searchParams] = useSearchParams();
    const { showError, showSuccess } = useSnackbar();
    const userInfo = useUserStore((state) => state.userInfo);

    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [commentText, setCommentText] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [isLiked, setIsLiked] = useState(false);
    const [likeCount, setLikeCount] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerIndex, setViewerIndex] = useState(0);
    const [commentPage, setCommentPage] = useState(1);
    const [commentTotalPages, setCommentTotalPages] = useState(0);
    const [isLoadingMoreComments, setIsLoadingMoreComments] = useState(false);
    const [commentCount, setCommentCount] = useState(0);
    const [showPostMenu, setShowPostMenu] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showCommentMenu, setShowCommentMenu] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState(null);
    const [isDeletingComment, setIsDeletingComment] = useState(false);
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingCommentText, setEditingCommentText] = useState("");
    const [replyingTo, setReplyingTo] = useState(null);
    const [expandedReplies, setExpandedReplies] = useState(new Set());
    const [showOtherPostMenu, setShowOtherPostMenu] = useState(false);
    const [showReportSheet, setShowReportSheet] = useState(false);
    const [reportTarget, setReportTarget] = useState(null); // { type, id }
    const [selectedReason, setSelectedReason] = useState(null);
    const [reportDescription, setReportDescription] = useState("");
    const [isReporting, setIsReporting] = useState(false);
    const [showBlockConfirm, setShowBlockConfirm] = useState(false);
    const [blockTargetId, setBlockTargetId] = useState(null);
    const [isBlocking, setIsBlocking] = useState(false);
    const [showOtherCommentMenu, setShowOtherCommentMenu] = useState(false);
    const [otherCommentTarget, setOtherCommentTarget] = useState(null);
const [highlightedCommentId, setHighlightedCommentId] = useState(null);
    const bottomCommentRef = useRef(null);
    const commentObserverRef = useRef(null);
    const commentRef = useRef(null);
    const editCommentRef = useRef(null);
    const commentMention = useMentionAutocomplete({
        value: commentText,
        setValue: setCommentText,
        textareaRef: commentRef,
        maxLength: 250,
    });
    const editMention = useMentionAutocomplete({
        value: editingCommentText,
        setValue: setEditingCommentText,
        textareaRef: editCommentRef,
        maxLength: 250,
    });

    // 헬퍼: root/reply 모두에서 댓글 업데이트
    const updateCommentOrReply = (list, targetId, updateFn) =>
        list.map((c) => {
            if (c.id === targetId) return updateFn(c);
            if (c.replies?.length) {
                const updatedReplies = c.replies.map((r) =>
                    r.id === targetId ? updateFn(r) : r
                );
                return { ...c, replies: updatedReplies };
            }
            return c;
        });

    // 헬퍼: root/reply 모두에서 댓글 찾기
    const findCommentOrReply = (list, targetId) => {
        for (const c of list) {
            if (c.id === targetId) return c;
            const found = c.replies?.find((r) => r.id === targetId);
            if (found) return found;
        }
        return null;
    };

    // 게시글 상세 + 댓글 로딩
    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [postResult, commentsResult] = await Promise.all([
                getPostDetail(postId),
                getComments(postId, 1),
            ]);

            if (postResult.success) {
                setPost(postResult.data);
                setIsLiked(postResult.data.is_liked);
                setLikeCount(postResult.data.like_count);
                setCommentCount(postResult.data.comment_count || 0);
            } else {
                setNotFound(true);
            }

            if (commentsResult.success) {
                setComments(commentsResult.data || []);
                setCommentPage(1);
                setCommentTotalPages(commentsResult.meta.totalPages);
            }
        } catch (error) {
            console.error("게시글 로딩 실패:", error);
            showError("게시글을 불러오지 못했습니다.");
        } finally {
            setIsLoading(false);
        }
    }, [postId, showError]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // ?comment= 파라미터로 댓글 앵커링
    useEffect(() => {
        const commentParam = searchParams.get("comment");
        if (!commentParam || comments.length === 0 || isLoading) return;

        const targetId = Number(commentParam);

        // 대댓글이면 부모 스레드를 펼치기
        for (const c of comments) {
            if (c.replies?.some((r) => r.id === targetId)) {
                setExpandedReplies((prev) => new Set(prev).add(c.id));
                break;
            }
        }

        setHighlightedCommentId(targetId);

        // DOM 업데이트 후 스크롤
        setTimeout(() => {
            const el = document.getElementById(`comment-${targetId}`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
    }, [comments, isLoading, searchParams]);

    // Pull-to-Refresh
    useRefreshCallback(() => loadData());

    // 댓글 무한 스크롤
    useEffect(() => {
        if (!bottomCommentRef.current) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (
                    entry.isIntersecting &&
                    !isLoadingMoreComments &&
                    commentPage < commentTotalPages
                ) {
                    const nextPage = commentPage + 1;
                    setCommentPage(nextPage);
                    setIsLoadingMoreComments(true);
                    getComments(postId, nextPage)
                        .then((result) => {
                            if (result.success) {
                                setCommentTotalPages(result.meta.totalPages);
                                setComments((prev) => {
                                    const ids = new Set(prev.map((c) => c.id));
                                    const newItems = result.data.filter((c) => !ids.has(c.id));
                                    return [...prev, ...newItems];
                                });
                            }
                        })
                        .catch(() => {})
                        .finally(() => setIsLoadingMoreComments(false));
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(bottomCommentRef.current);
        commentObserverRef.current = observer;
        return () => observer.disconnect();
    }, [commentPage, commentTotalPages, isLoadingMoreComments, postId]);

    // 뒤로가기
    const goBack = useGoBack();
    const handleBack = useCallback(() => {
        goBack();
    }, [goBack]);

    // 게시글 좋아요 (낙관적 업데이트)
    const handlePostLike = useCallback(async () => {
        if (!userInfo) {
            navigate('/login');
            return;
        }
        setIsLiked((prev) => !prev);
        setLikeCount((prev) => (isLiked ? prev - 1 : prev + 1));
        try {
            await togglePostLike(postId);
        } catch {
            setIsLiked((prev) => !prev);
            setLikeCount((prev) => (isLiked ? prev + 1 : prev - 1));
        }
    }, [postId, isLiked, userInfo, navigate]);

    // 댓글 좋아요 (낙관적 업데이트)
    const handleCommentLike = useCallback(async (commentId) => {
        if (!userInfo) {
            navigate('/login');
            return;
        }
        const toggleLike = (c) => ({
            ...c,
            is_liked: !c.is_liked,
            like_count: c.is_liked ? c.like_count - 1 : c.like_count + 1,
        });
        setComments((prev) => updateCommentOrReply(prev, commentId, toggleLike));
        try {
            await toggleCommentLike(commentId);
        } catch {
            setComments((prev) => updateCommentOrReply(prev, commentId, toggleLike));
        }
    }, [userInfo, navigate]);

    // 댓글 작성
    const { serialize: serializeComment } = commentMention;
    const handleSubmitComment = useCallback(async () => {
        if (!userInfo) {
            navigate('/login');
            return;
        }
        if (!commentText.trim() || isSubmitting) return;
        const serialized = serializeComment().trim();
        if (serialized.length > SERVER_MAX_COMMENT_CONTENT) {
            showError("멘션이 많아 글자 수 제한(500자)을 초과했어요.");
            return;
        }
        setIsSubmitting(true);
        const parentId = replyingTo?.id || null;
        try {
            const result = await createComment(postId, serialized, parentId);
            if (result.success) {
                setCommentCount((prev) => prev + 1);
                setCommentText("");
                setReplyingTo(null);
                const textarea = document.querySelector('.comment-input');
                if (textarea) textarea.style.height = 'auto';
                // 낙관적 추가: 서버 응답 데이터를 즉시 로컬 state에 삽입
                const newComment = result.data;
                if (parentId) {
                    setComments((prev) =>
                        prev.map((c) =>
                            c.id === parentId
                                ? { ...c, replies: [...(c.replies || []), newComment] }
                                : c
                        )
                    );
                    setExpandedReplies((prev) => new Set(prev).add(parentId));
                } else {
                    setComments((prev) => [...prev, newComment]);
                }
                // 스크롤 + 하이라이트
                setHighlightedCommentId(newComment.id);
                setTimeout(() => {
                    document.getElementById(`comment-${newComment.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
                setTimeout(() => setHighlightedCommentId(null), 2000);
            }
        } catch (error) {
            showError("댓글 작성에 실패했습니다.");
        } finally {
            setIsSubmitting(false);
        }
    }, [postId, commentText, isSubmitting, showError, userInfo, navigate, replyingTo, serializeComment]);

    // 소유권 판별 — public_id 가 확정된 뒤에만 유효하다.
    // 미확정 구간에서 판정하면 본인 글이 타인 글로 분류돼 자기신고·자기차단으로 이어진다.
    const identityStatus = usePublicIdentityStatus();
    const canJudgeOwnership = identityStatus === IDENTITY_STATUS.RESOLVED;
    const isPostOwner = canJudgeOwnership && isSameUser(userInfo, post);
    const isCommentOwner = (comment) =>
        canJudgeOwnership && isSameUser(userInfo, comment);

    // 게시글 삭제
    const handleDeletePost = useCallback(async () => {
        if (isDeleting) return;
        setIsDeleting(true);
        try {
            const result = await deletePost(postId);
            if (result.success) {
                navigate("/community");
            }
        } catch (error) {
            showError("게시글 삭제에 실패했습니다.");
        } finally {
            setIsDeleting(false);
        }
    }, [postId, isDeleting, navigate, showError]);

    // 댓글 삭제
    const handleDeleteComment = useCallback(async () => {
        if (isDeletingComment || !commentToDelete) return;
        setIsDeletingComment(true);
        try {
            const result = await deleteComment(commentToDelete);
            if (result.success) {
                const isRoot = comments.some((c) => c.id === commentToDelete);
                if (isRoot) {
                    const rootComment = comments.find((c) => c.id === commentToDelete);
                    const hasReplies = rootComment?.replies?.length > 0;
                    if (hasReplies) {
                        // 대댓글이 있는 루트 댓글: 소프트 삭제 (플레이스홀더 표시)
                        setComments((prev) =>
                            prev.map((c) =>
                                c.id === commentToDelete
                                    ? { ...c, content: null, is_deleted: true }
                                    : c
                            )
                        );
                        setCommentCount((prev) => prev - 1);
                    } else {
                        // 대댓글 없는 루트 댓글: 목록에서 제거
                        setComments((prev) => prev.filter((c) => c.id !== commentToDelete));
                        setCommentCount((prev) => prev - 1);
                    }
                } else {
                    // 대댓글 삭제
                    setComments((prev) =>
                        prev.map((c) => {
                            if (!c.replies?.some((r) => r.id === commentToDelete)) return c;
                            const newReplies = c.replies.filter((r) => r.id !== commentToDelete);
                            // 대댓글 모두 삭제되고 루트도 삭제 상태면 전체 제거
                            if (newReplies.length === 0 && c.is_deleted) {
                                return null;
                            }
                            return { ...c, replies: newReplies };
                        }).filter(Boolean)
                    );
                    setCommentCount((prev) => prev - 1);
                }
                setCommentToDelete(null);
            }
        } catch (error) {
            showError("댓글 삭제에 실패했습니다.");
        } finally {
            setIsDeletingComment(false);
        }
    }, [commentToDelete, isDeletingComment, showError, comments]);

    // 댓글 수정 시작
    const { serialize: serializeEdit, initFromRaw: initEditFromRaw } = editMention;
    const handleStartEditComment = useCallback((comment) => {
        setShowCommentMenu(false);
        setCommentToDelete(null);
        setEditingCommentId(comment.id);
        initEditFromRaw(comment.content);
    }, [initEditFromRaw]);

    // 댓글 수정 저장
    const handleSaveEditComment = useCallback(async () => {
        if (!editingCommentId || !editingCommentText.trim()) return;
        const serializedEdit = serializeEdit().trim();
        if (serializedEdit.length > SERVER_MAX_COMMENT_CONTENT) {
            showError("멘션이 많아 글자 수 제한(500자)을 초과했어요.");
            return;
        }
        try {
            const result = await updateComment(editingCommentId, serializedEdit);
            if (result.success) {
                setComments((prev) =>
                    updateCommentOrReply(prev, editingCommentId, (c) => ({
                        ...c,
                        content: serializedEdit,
                    }))
                );
            }
        } catch (error) {
            showError("댓글 수정에 실패했습니다.");
        } finally {
            setEditingCommentId(null);
            setEditingCommentText("");
        }
    }, [editingCommentId, editingCommentText, showError, serializeEdit]);

    // 댓글 수정 취소
    const handleCancelEditComment = useCallback(() => {
        setEditingCommentId(null);
        setEditingCommentText("");
    }, []);

    // 신고 사유 목록
    const REPORT_REASONS = [
        { value: 'spam', label: '스팸' },
        { value: 'harassment', label: '괴롭힘' },
        { value: 'hate_speech', label: '혐오 발언' },
        { value: 'violence', label: '폭력' },
        { value: 'inappropriate', label: '부적절한 콘텐츠' },
        { value: 'other', label: '기타' },
    ];

    // 신고 제출
    const handleSubmitReport = useCallback(async () => {
        if (!selectedReason || !reportTarget || isReporting) return;
        setIsReporting(true);
        try {
            const result = await createReport({
                target_type: reportTarget.type,
                target_id: reportTarget.id,
                reason: selectedReason,
                description: selectedReason === 'other' ? reportDescription : undefined,
            });
            if (result.success) {
                showSuccess('신고가 접수되었습니다.');
            }
        } catch {
            showError('신고에 실패했습니다.');
        } finally {
            setIsReporting(false);
            setShowReportSheet(false);
            setReportTarget(null);
            setSelectedReason(null);
            setReportDescription("");
        }
    }, [selectedReason, reportTarget, reportDescription, isReporting, showSuccess, showError]);

    // 차단 실행
    const handleBlockUser = useCallback(async () => {
        if (!blockTargetId || isBlocking) return;
        setIsBlocking(true);
        try {
            const result = await blockUser(blockTargetId);
            if (result.success) {
                showSuccess('사용자를 차단했습니다.');
                navigate('/community');
            }
        } catch {
            showError('차단에 실패했습니다.');
        } finally {
            setIsBlocking(false);
            setShowBlockConfirm(false);
            setBlockTargetId(null);
        }
    }, [blockTargetId, isBlocking, navigate, showSuccess, showError]);

    // 이미지 탭 → 풀스크린 뷰어
    const openViewer = useCallback((index) => {
        setViewerIndex(index);
        setViewerOpen(true);
    }, []);

    // 답글 버튼 클릭
    const handleReply = useCallback((comment, parentId) => {
        if (!userInfo) {
            navigate('/login');
            return;
        }
        const replyId = parentId || comment.id;
        setReplyingTo({ id: replyId, author_nickname: comment.author_nickname });
        setCommentText("");
        const textarea = document.querySelector('.comment-input');
        if (textarea) textarea.focus();
    }, [userInfo, navigate]);

    // 댓글 렌더링 (root/reply 공통)
    const renderComment = (comment, isReply) => {
        // 삭제된 댓글 플레이스홀더
        if (comment.is_deleted) {
            return (
                <div key={comment.id} id={`comment-${comment.id}`} className="comment-item comment-item--deleted">
                    <div className="comment-deleted-placeholder">
                        삭제된 댓글입니다.
                    </div>
                </div>
            );
        }
        return (
        <div key={comment.id} id={`comment-${comment.id}`} className={`comment-item${isReply ? ' comment-item--reply' : ''}${highlightedCommentId === comment.id ? ' comment-item--new' : ''}`}>
            {comment.author_profile_image ? (
                <img
                    src={comment.author_profile_image}
                    alt=""
                    className="comment-avatar"
                    role="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (getPublicUserKey(comment)) {
                            window.open(
                                `/community/user/${encodeURIComponent(getPublicUserKey(comment))}`,
                                "_blank",
                            );
                        }
                    }}
                />
            ) : (
                <div
                    className="comment-avatar-placeholder"
                    role="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (getPublicUserKey(comment)) {
                            window.open(
                                `/community/user/${encodeURIComponent(getPublicUserKey(comment))}`,
                                "_blank",
                            );
                        }
                    }}
                >
                    {(comment.author_nickname || "?").charAt(0)}
                </div>
            )}
            <div className="comment-body">
                <div className="comment-header">
                    <span
                        className="comment-nickname"
                        role="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (getPublicUserKey(comment)) {
                                window.open(
                                    `/community/user/${encodeURIComponent(getPublicUserKey(comment))}`,
                                    "_blank",
                                );
                            }
                        }}
                    >
                        {comment.author_nickname}
                    </span>
                    <span className="comment-time">
                        {formatTime(comment.created_at)}
                    </span>
                </div>
                {editingCommentId === comment.id ? (
                    <div className="comment-edit-area">
                        <textarea
                            ref={editCommentRef}
                            className="comment-edit-input"
                            value={editingCommentText}
                            onChange={editMention.handleChange}
                            onKeyDown={editMention.handleKeyDown}
                            onBlur={editMention.handleBlur}
                            autoFocus
                            rows={2}
                            maxLength={250}
                            aria-label="댓글 수정"
                            role="combobox"
                            aria-expanded={editMention.open}
                            aria-controls={editMention.listId}
                            aria-autocomplete="list"
                            aria-activedescendant={editMention.open ? editMention.activeOptionId : undefined}
                        />
                        <MentionDropdown
                            id={editMention.listId}
                            open={editMention.open}
                            results={editMention.results}
                            loading={editMention.loading}
                            activeIndex={editMention.activeIndex}
                            onSelect={editMention.selectUser}
                            placement="above"
                        />
                        <span className={`comment-char-counter${editingCommentText.length >= 230 ? ' near-limit' : ''}`}>
                            {editingCommentText.length}/250
                        </span>
                        <div className="comment-edit-actions">
                            <button
                                className="comment-edit-cancel"
                                onClick={handleCancelEditComment}
                            >
                                취소
                            </button>
                            <button
                                className="comment-edit-save"
                                onClick={handleSaveEditComment}
                                disabled={!editingCommentText.trim()}
                            >
                                저장
                            </button>
                        </div>
                    </div>
                ) : (
                    <p className="comment-text">
                        {renderWithMentions(comment.content)}
                    </p>
                )}
                <div className="comment-actions">
                    <button
                        className="comment-like-btn"
                        onClick={() => handleCommentLike(comment.id)}
                    >
                        <svg
                            className="comment-like-icon"
                            viewBox="0 0 16 16"
                            fill="none"
                        >
                            <path
                                d="M8 14s-5.5-3.5-5.5-7.5A3.2 3.2 0 0 1 8 4a3.2 3.2 0 0 1 5.5 2.5C13.5 10.5 8 14 8 14z"
                                fill={comment.is_liked ? "#e40000" : "none"}
                                stroke={comment.is_liked ? "#e40000" : "#757b80"}
                                strokeWidth="1.2"
                            />
                        </svg>
                        {comment.like_count > 0 && <span className="comment-like-count">
                            {comment.like_count}
                        </span>}
                    </button>
                    <button
                        className="comment-reply-btn"
                        onClick={() => handleReply(comment, isReply ? comment.parent_id : null)}
                    >
                        <svg className="comment-reply-icon" viewBox="0 0 16 16" fill="none">
                            <path
                                d="M13.5 8c0 3.038-2.462 5.5-5.5 5.5a5.66 5.66 0 0 1-2.75-.708L2.5 13.5l.708-2.75A5.66 5.66 0 0 1 2.5 8C2.5 4.962 4.962 2.5 8 2.5S13.5 4.962 13.5 8z"
                                stroke="#757b80"
                                strokeWidth="1.2"
                            />
                        </svg>
                        {!isReply && (comment.replies?.length || 0) > 0 && <span className="comment-reply-count">
                            {comment.replies.length}
                        </span>}
                    </button>
                </div>
            </div>
            {userInfo && canJudgeOwnership && editingCommentId !== comment.id && (
                <button
                    className="comment-menu-btn"
                    onClick={() => {
                        if (isCommentOwner(comment)) {
                            setCommentToDelete(comment.id);
                            setShowCommentMenu(true);
                        } else {
                            setOtherCommentTarget(comment);
                            setShowOtherCommentMenu(true);
                        }
                    }}
                    aria-label="댓글 메뉴"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="3" r="1.2" fill="#a4adb2" />
                        <circle cx="8" cy="8" r="1.2" fill="#a4adb2" />
                        <circle cx="8" cy="13" r="1.2" fill="#a4adb2" />
                    </svg>
                </button>
            )}
        </div>
        );
    };

    // 헤더 렌더링
    const renderHeader = () => (
        <SubHeader title="게시글" customGoback={handleBack} />
    );

    // 로딩
    if (isLoading) {
        return (
            <div className="community-page community-detail">
                {renderHeader()}
                <div className="detail-content">
                    <div className="skeleton-post">
                        <div className="skeleton-author-row">
                            <div className="skeleton-avatar shimmer" />
                            <div className="skeleton-author-info">
                                <div className="skeleton-line skeleton-line--short shimmer" />
                                <div className="skeleton-line skeleton-line--xshort shimmer" />
                            </div>
                        </div>
                        <div className="skeleton-text">
                            <div className="skeleton-line shimmer" />
                            <div className="skeleton-line shimmer" />
                            <div className="skeleton-line skeleton-line--medium shimmer" />
                        </div>
                        <div className="skeleton-image shimmer" />
                        <div className="skeleton-actions">
                            <div className="skeleton-line skeleton-line--xshort shimmer" />
                            <div className="skeleton-line skeleton-line--xshort shimmer" />
                        </div>
                    </div>
                </div>
                <div className="skeleton-divider" />
                <div className="skeleton-comments">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="skeleton-comment">
                            <div className="skeleton-comment-avatar shimmer" />
                            <div className="skeleton-comment-body">
                                <div className="skeleton-line skeleton-line--short shimmer" />
                                <div className="skeleton-line shimmer" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // 404
    if (notFound || !post) {
        return (
            <div className="community-page community-detail">
                {renderHeader()}
                <div className="detail-content">
                    <div className="empty-state">
                        <h3 className="empty-title">
                            게시글을 찾을 수 없습니다
                        </h3>
                        <p className="empty-description">
                            삭제되었거나 존재하지 않는 게시글입니다.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const mediaUrls = post.media_urls || [];

    return (
        <div className="community-page community-detail">
            <Seo
                title={
                    post.content
                        ? `${post.content.replace(/\s+/g, ' ').slice(0, 40)}${post.content.length > 40 ? '…' : ''}`
                        : '커뮤니티 게시글'
                }
                description={post.content ? post.content.replace(/\s+/g, ' ').slice(0, 150) : undefined}
                image={mediaUrls[0]}
                type="article"
            />
            {renderHeader()}

            {/* 게시글 본문 */}
            <div className="detail-content">
                {/* 작성자 행 */}
                <div className="author-row">
                    {post.author_profile_image ? (
                        <img
                            src={post.author_profile_image}
                            alt=""
                            className="author-avatar clickable"
                            onClick={() => { if (getPublicUserKey(post)) window.open(`/community/user/${encodeURIComponent(getPublicUserKey(post))}`, "_blank"); }}
                        />
                    ) : (
                        <div
                            className="author-avatar-placeholder clickable"
                            onClick={() => { if (getPublicUserKey(post)) window.open(`/community/user/${encodeURIComponent(getPublicUserKey(post))}`, "_blank"); }}
                        >
                            {(
                                post.author_nickname ||
                                post.author_name ||
                                "?"
                            ).charAt(0)}
                        </div>
                    )}
                    <div className="author-info">
                        <div className="author-name-row">
                            <span
                                className="author-nickname clickable"
                                onClick={() => { if (getPublicUserKey(post)) window.open(`/community/user/${encodeURIComponent(getPublicUserKey(post))}`, "_blank"); }}
                            >
                                {post.author_nickname || post.author_name}
                            </span>
                            {post.post_category && (
                                <>
                                    <span className="separator">›</span>
                                    <span className="activity-type">
                                        {categoryMap[post.post_category] || post.post_category}
                                    </span>
                                </>
                            )}
                        </div>
                        <span className="post-time">
                            {formatTime(post.created_at)}
                        </span>
                    </div>
                    {userInfo && canJudgeOwnership && (
                        <button
                            className="post-menu-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (isPostOwner) {
                                    setShowPostMenu(true);
                                } else {
                                    setShowOtherPostMenu(true);
                                }
                            }}
                            aria-label="게시글 메뉴"
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <circle cx="10" cy="4" r="1.5" fill="#757b80" />
                                <circle cx="10" cy="10" r="1.5" fill="#757b80" />
                                <circle cx="10" cy="16" r="1.5" fill="#757b80" />
                            </svg>
                        </button>
                    )}
                </div>

                {/* 본문 */}
                <p className="post-text">{renderWithMentions(post.content)}</p>

                {/* 이미지 캐러셀 */}
                <MediaCarousel
                    mediaUrls={mediaUrls}
                    onImageClick={(idx) => openViewer(idx)}
                />

                {/* 액션 바 */}
                <div className="action-bar">
                    <button className="action-item" onClick={handlePostLike}>
                        <svg
                            className="action-icon"
                            viewBox="0 0 20 20"
                            fill="none"
                        >
                            <path
                                d="M10 17.5s-7-4.5-7-9.5A4 4 0 0 1 10 5a4 4 0 0 1 7 3c0 5-7 9.5-7 9.5z"
                                fill={isLiked ? "#e40000" : "none"}
                                stroke={isLiked ? "#e40000" : "#464a4d"}
                                strokeWidth="1.5"
                            />
                        </svg>
                        {likeCount > 0 && <span className="action-count">{likeCount}</span>}
                    </button>
                    <button className="action-item">
                        <svg
                            className="action-icon"
                            viewBox="0 0 20 20"
                            fill="none"
                        >
                            <path
                                d="M17 10c0 3.866-3.134 7-7 7a7.2 7.2 0 0 1-3.5-.9L3 17l.9-3.5A7.2 7.2 0 0 1 3 10c0-3.866 3.134-7 7-7s7 3.134 7 7z"
                                stroke="#464a4d"
                                strokeWidth="1.5"
                                fill="none"
                            />
                        </svg>
                        {commentCount > 0 && <span className="action-count">
                            {commentCount}
                        </span>}
                    </button>
                </div>
            </div>

            {/* 구분선 */}
            <div className="section-divider" />

            {/* 댓글 섹션 */}
            <div className="comments-section">
                <h3 className="comments-title">댓글 {commentCount}</h3>

                {comments.length === 0 ? (
                    <p className="comments-empty">
                        아직 댓글이 없습니다. 첫 댓글을 남겨보세요!
                    </p>
                ) : (
                    <>
                        {comments.map((comment) => (
                            <div key={comment.id} className="comment-thread">
                                {renderComment(comment, false)}
                                {/* 답글 토글 버튼 */}
                                {comment.replies?.length > 0 && (
                                    <button
                                        className="replies-toggle"
                                        onClick={() =>
                                            setExpandedReplies((prev) => {
                                                const next = new Set(prev);
                                                if (next.has(comment.id)) {
                                                    next.delete(comment.id);
                                                } else {
                                                    next.add(comment.id);
                                                }
                                                return next;
                                            })
                                        }
                                    >
                                        <span className="replies-toggle-line" />
                                        <span className="replies-toggle-text">
                                            {expandedReplies.has(comment.id)
                                                ? "답글 숨기기"
                                                : `답글 ${comment.replies.length}개 보기`}
                                        </span>
                                    </button>
                                )}
                                {/* 답글 목록 */}
                                {comment.replies?.length > 0 && (
                                    <div className={`replies-list${expandedReplies.has(comment.id) ? ' replies-list--expanded' : ''}`}>
                                        {comment.replies.map((reply) => renderComment(reply, true))}
                                    </div>
                                )}
                            </div>
                        ))}
                        <div ref={bottomCommentRef} />
                        {isLoadingMoreComments && (
                            <div className="loading-more">댓글 로딩 중...</div>
                        )}
                    </>
                )}
            </div>

            {/* 댓글 입력 바 (하단 플로팅) */}
            <div className="comment-input-bar">
                {replyingTo && (
                    <div className="reply-indicator">
                        <span className="reply-indicator-text">
                            {replyingTo.author_nickname}님에게 답글 작성 중
                        </span>
                        <button
                            className="reply-indicator-close"
                            onClick={() => setReplyingTo(null)}
                        >
                            &times;
                        </button>
                    </div>
                )}
                <MentionDropdown
                    id={commentMention.listId}
                    open={commentMention.open}
                    results={commentMention.results}
                    loading={commentMention.loading}
                    activeIndex={commentMention.activeIndex}
                    onSelect={commentMention.selectUser}
                    placement="above"
                />
                <div className="comment-input-container">
                    <textarea
                        ref={commentRef}
                        className="comment-input"
                        placeholder={replyingTo ? `${replyingTo.author_nickname}님에게 답글...` : "댓글을 입력하세요..."}
                        value={commentText}
                        rows={1}
                        maxLength={250}
                        aria-label="댓글 내용"
                        role="combobox"
                        aria-expanded={commentMention.open}
                        aria-controls={commentMention.listId}
                        aria-autocomplete="list"
                        aria-activedescendant={commentMention.open ? commentMention.activeOptionId : undefined}
                        onFocus={() => {
                            if (!userInfo) {
                                navigate('/login');
                            }
                        }}
                        onBlur={commentMention.handleBlur}
                        onKeyDown={commentMention.handleKeyDown}
                        onChange={(e) => {
                            commentMention.handleChange(e);
                            e.target.style.height = 'auto';
                            e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px';
                        }}
                    />
                    {commentText.length > 0 && (
                        <span className={`comment-char-counter${commentText.length >= 230 ? ' near-limit' : ''}`}>
                            {commentText.length}/250
                        </span>
                    )}
                    <button
                        className="comment-submit-btn"
                        disabled={!commentText.trim() || isSubmitting}
                        onClick={handleSubmitComment}
                    >
                        게시
                    </button>
                </div>
            </div>

            {/* 풀스크린 이미지 뷰어 */}
            {viewerOpen && (
                <MediaViewer
                    media={mediaUrls.map(url => ({
                        url,
                        type: /\.(mp4|mov|webm|avi|mkv)(\?|$)/i.test(url) ? 'video' : 'image',
                    }))}
                    initialIndex={viewerIndex}
                    onClose={() => setViewerOpen(false)}
                />
            )}

            {/* 게시글 더보기 메뉴 */}
            <BottomSheet
                isOpen={showPostMenu}
                onClose={() => setShowPostMenu(false)}
                height="auto"
            >
                <div className="post-action-menu">
                    <button
                        className="post-action-item"
                        onClick={() => {
                            setShowPostMenu(false);
                            navigate(`/community/edit/${postId}`);
                        }}
                    >
                        수정하기
                    </button>
                    <button
                        className="post-action-item delete"
                        onClick={() => {
                            setShowPostMenu(false);
                            handleDeletePost();
                        }}
                        disabled={isDeleting}
                    >
                        삭제하기
                    </button>
                </div>
            </BottomSheet>

            {/* 댓글 더보기 메뉴 (본인 댓글) */}
            <BottomSheet
                isOpen={showCommentMenu}
                onClose={() => {
                    setShowCommentMenu(false);
                    setCommentToDelete(null);
                }}
                height="auto"
            >
                <div className="post-action-menu">
                    <button
                        className="post-action-item"
                        onClick={() => {
                            const comment = findCommentOrReply(comments, commentToDelete);
                            if (comment) handleStartEditComment(comment);
                        }}
                    >
                        수정하기
                    </button>
                    <button
                        className="post-action-item delete"
                        onClick={() => {
                            setShowCommentMenu(false);
                            handleDeleteComment();
                        }}
                        disabled={isDeletingComment}
                    >
                        삭제하기
                    </button>
                </div>
            </BottomSheet>

            {/* 타인 게시글 메뉴 (신고/차단) */}
            <BottomSheet
                isOpen={showOtherPostMenu}
                onClose={() => setShowOtherPostMenu(false)}
                height="auto"
            >
                <div className="post-action-menu">
                    <button
                        className="post-action-item"
                        onClick={() => {
                            setShowOtherPostMenu(false);
                            setReportTarget({ type: 'post', id: post?.id });
                            setShowReportSheet(true);
                        }}
                    >
                        신고하기
                    </button>
                    <button
                        className="post-action-item delete"
                        onClick={() => {
                            setShowOtherPostMenu(false);
                            setBlockTargetId(getPublicUserKey(post));
                            setShowBlockConfirm(true);
                        }}
                    >
                        이 사용자 차단
                    </button>
                </div>
            </BottomSheet>

            {/* 타인 댓글 메뉴 (신고) */}
            <BottomSheet
                isOpen={showOtherCommentMenu}
                onClose={() => {
                    setShowOtherCommentMenu(false);
                    setOtherCommentTarget(null);
                }}
                height="auto"
            >
                <div className="post-action-menu">
                    <button
                        className="post-action-item"
                        onClick={() => {
                            setShowOtherCommentMenu(false);
                            setReportTarget({ type: 'comment', id: otherCommentTarget?.id });
                            setShowReportSheet(true);
                        }}
                    >
                        신고하기
                    </button>
                </div>
            </BottomSheet>

            {/* 신고 사유 선택 */}
            <BottomSheet
                isOpen={showReportSheet}
                onClose={() => {
                    setShowReportSheet(false);
                    setReportTarget(null);
                    setSelectedReason(null);
                    setReportDescription("");
                }}
                title="신고 사유 선택"
                height="auto"
            >
                <div className="report-reason-menu">
                    {REPORT_REASONS.map((r) => (
                        <button
                            key={r.value}
                            className={`report-reason-item${selectedReason === r.value ? ' report-reason-item--selected' : ''}`}
                            onClick={() => setSelectedReason(r.value)}
                        >
                            {r.label}
                            {selectedReason === r.value && (
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M3 8.5l3.5 3.5 6.5-7" stroke="#0066ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                        </button>
                    ))}
                    {selectedReason === 'other' && (
                        <textarea
                            className="report-description"
                            placeholder="상세 내용을 입력해주세요"
                            value={reportDescription}
                            onChange={(e) => setReportDescription(e.target.value)}
                            rows={3}
                        />
                    )}
                    <button
                        className="report-submit-btn"
                        disabled={!selectedReason || isReporting}
                        onClick={handleSubmitReport}
                    >
                        {isReporting ? '처리 중...' : '신고하기'}
                    </button>
                </div>
            </BottomSheet>

            {/* 차단 확인 */}
            <BottomSheet
                isOpen={showBlockConfirm}
                onClose={() => {
                    setShowBlockConfirm(false);
                    setBlockTargetId(null);
                }}
                title="사용자 차단"
                height="auto"
            >
                <div className="block-confirm-menu">
                    <p className="block-confirm-message">
                        차단하면 이 사용자의 글이 보이지 않습니다. 차단하시겠습니까?
                    </p>
                    <div className="block-confirm-actions">
                        <button
                            className="block-confirm-cancel"
                            onClick={() => {
                                setShowBlockConfirm(false);
                                setBlockTargetId(null);
                            }}
                        >
                            취소
                        </button>
                        <button
                            className="block-confirm-submit"
                            disabled={isBlocking}
                            onClick={handleBlockUser}
                        >
                            {isBlocking ? '처리 중...' : '차단하기'}
                        </button>
                    </div>
                </div>
            </BottomSheet>

        </div>
    );
};

export default CommunityDetail;
